
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Facebook, Instagram, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Details", path: "/details" },
    { name: "Gallery", path: "/gallery" },
    { name: "Reviews", path: "/reviews" },
    { name: "Contact", path: "/contact" },
    { name: "Book", path: "/book" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-6 md:px-10",
        isScrolled
          ? "bg-white/80 backdrop-blur-md shadow-sm py-3"
          : "bg-white shadow-sm py-6" // Changed from transparent to white
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link
          to="/"
          className="text-cottage-800 font-playfair text-2xl md:text-3xl font-semibold transition-transform hover:scale-105 duration-300"
        >
          The Cottage <span className="text-cottage-500 font-normal text-lg md:text-xl">at Flowerpot</span>
        </Link>

        <nav className="hidden md:flex items-center space-x-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all duration-300",
                location.pathname === link.path
                  ? "text-cottage-700 bg-cottage-50/80"
                  : "text-cottage-600 hover:text-cottage-800 hover:bg-cottage-50/50"
              )}
            >
              {link.name}
            </Link>
          ))}
          
          <div className="flex items-center gap-2 pl-2 border-l border-cottage-200">
            <a
              href="https://www.facebook.com/flowerpotcottage"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cottage-600 hover:text-cottage-800 transition-colors p-2"
              aria-label="Visit our Facebook page"
            >
              <Facebook size={20} />
            </a>
            <a
              href="https://www.instagram.com/flowerpot_cottage"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cottage-600 hover:text-cottage-800 transition-colors p-2"
              aria-label="Visit our Instagram page"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://wa.me/61403860488"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cottage-600 hover:text-cottage-800 transition-colors p-2"
              aria-label="Contact us on WhatsApp"
            >
              <MessageCircle size={20} />
            </a>
          </div>
        </nav>

        <button
          className="md:hidden text-cottage-700 focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMobile && (
        <div
          className={cn(
            "fixed inset-0 bg-white z-40 transition-transform duration-300 ease-in-out pt-20",
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <nav className="flex flex-col items-center space-y-6 p-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "px-4 py-3 w-full text-center text-lg font-medium rounded-md transition-all duration-300",
                  location.pathname === link.path
                    ? "text-cottage-700 bg-cottage-50"
                    : "text-cottage-600 hover:text-cottage-800 hover:bg-cottage-50/50"
                )}
              >
                {link.name}
              </Link>
            ))}
            
            
            <div className="flex items-center justify-center gap-6 pt-4 border-t border-cottage-200">
              <a
                href="https://www.facebook.com/flowerpotcottage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cottage-600 hover:text-cottage-800 transition-colors p-2"
                aria-label="Visit our Facebook page"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://www.instagram.com/flowerpot_cottage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cottage-600 hover:text-cottage-800 transition-colors p-2"
                aria-label="Visit our Instagram page"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://wa.me/61403860488"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cottage-600 hover:text-cottage-800 transition-colors p-2"
                aria-label="Contact us on WhatsApp"
              >
                <MessageCircle size={24} />
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
